import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import NumberFormat from "react-number-format";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Typography,
  Paper,
  Grid,
  Box,
  useMediaQuery,
  Dialog,
  Divider,
} from "@mui/material";
import { makeStyles, useTheme, withStyles } from "@mui/styles";

import { TimeLoader } from "@/_components";
import { langConversionHelper } from "@/_helpers";

import SecurePaymentMethod from "./SecurePaymentMethod";

import { getInvoiceEstimateSummary } from "../_redux/actions/paymentActions";

const CalculatingTypography = withStyles({
  root: {
    fontSize: 14,
    color: "#1c355e",
    fontWeight: 600,
    paddingTop: 2,
    paddingLeft: 10,
  },
})(Typography);

const useStyles = makeStyles((theme) => ({
  entireDialogPaper: {
    borderRadius: 0,
  },
  dialogPaper: {
    padding: theme.spacing(2),
    textAlign: "center",
    width: 340,
    borderRadius: 0,
  },
  divideMe: {
    marginTop: 10,
    marginBottom: 10,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "left",
    maxWidth: 600,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, .125)",
    marginTop: 50,
  },
  paymentPaper: {
    padding: theme.spacing(4),
    textAlign: "left",
    maxWidth: 600,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, .125)",
    marginTop: 50,
  },
  summaryHeader: {
    variant: "h1",
    fontWeight: 600,
    color: "#00175a",
    display: "flex",
    justifyContent: "center",
  },
  paymentHeader: {
    variant: "h6",
    fontWeight: 600,
    color: "#00175a",
    display: "flex",
    justifyContent: "center",
  },
  cardHeader: {
    fontSize: 20,
    marginTop: 55,
    fontWeight: 600,
  },
}));

function SecurePaySummary({
  getInvoiceEstimateSummary,
  setLoadingTime,
  setIsSuccess,
  loadingTime,
  validateSecurePaymentData,
  paymentMethod,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isXtraSmall = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);

  const StyledText = ({ text, isXtraSmall }) => (
    <strong
      style={{
        textDecoration: "underline",
        fontSize: isXtraSmall ? "10px" : "16px",
      }}
    >
      {text}
    </strong>
  );

  const GridItem = ({ xs, children, isXtraSmall }) => (
    <Grid item xs={xs} style={{ fontSize: isXtraSmall ? "10px" : "16px" }}>
      {children}
    </Grid>
  );

  useEffect(() => {
    let isMounted = true;

    let requestData = {
      vehicleId: validateSecurePaymentData.vehicleID,
      clientId: validateSecurePaymentData.clientID,
      locationId: validateSecurePaymentData.locationID,
      jurisdictionId: validateSecurePaymentData.jurisdictionID,
      serviceId: validateSecurePaymentData.serviceID,
      dayQuantity: validateSecurePaymentData.dayQuantity,
      earlyService: false,
      expedientShipping: false,
    };

    const fetchInvoiceSummary = async () => {
      setLoadingTime(true);
      try {
        const data = await getInvoiceEstimateSummary(requestData);
        if (isMounted) {
          setInvoiceItems(data?.fees);
          setSummaryRows([
            { label: "taxableAmount", value: data.taxableAmount },
            { label: "tax", value: data.tax },
            { label: "nonTaxableAmount", value: data.nonTaxableAmount },
            { label: "totalNewCharges", value: data.totalNew },
            { label: "priorAccountBalance", value: data.priorBalance },
            { label: "totalAmountDueNow", value: data.totalAmount },
          ]);
          setLoadingTime(false);
        }
      } catch (error) {
        if (isMounted) {
          console.log("Loading invoice estimate summary failed! " + error);
          setLoadingTime(false);
        }
      }
    };

    fetchInvoiceSummary();

    return () => {
      isMounted = false;
    };
  }, []);

  const SummaryRow = ({ label, value }) => (
    <>
      <Grid
        item
        xs={6}
        sx={{ textAlign: "left", fontSize: isXtraSmall ? "10px" : "16px" }}
      >
        {label === "totalAmountDueNow" ? (
          <strong>{t(`payments.${label}`)}</strong>
        ) : (
          t(`payments.${label}`)
        )}
      </Grid>
      <Grid
        item
        xs={6}
        sx={{
          textAlign: "right",
          fontSize: isXtraSmall ? "10px" : "16px",
          borderBottom:
            label === "nonTaxableAmount" || label === "priorAccountBalance"
              ? "solid 2px"
              : "0",
        }}
      >
        <NumberFormat
          value={value}
          displayType={"text"}
          thousandSeparator={true}
          prefix={"$ "}
          xs={1}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </Grid>
    </>
  );

  return (
    <>
      <Dialog
        open={loadingTime}
        onClose={() => setLoadingTime(false)}
        aria-labelledby="calculating-overlay"
        classes={{ paper: classes.entireDialogPaper }}
      >
        <Grid container direction="column">
          <Grid item>
            <Paper className={classes.dialogPaper} elevation={0}>
              <Grid container direction="column">
                <Grid item>
                  <CalculatingTypography>
                    {t("payments.calculatingPayment")}
                  </CalculatingTypography>
                </Grid>
                <Divider className={classes.divideMe} />
                <Grid
                  item
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <TimeLoader />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Dialog>

      {loadingTime ? null : (
        <>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography
                  variant="h6"
                  gutterBottom
                  className={classes.summaryHeader}
                  sx={{ fontSize: isXtraSmall ? "16px" : "20px" }}
                >
                  {t("payments.paymentSummary")}
                </Typography>
                <Grid container>
                  <Grid item xs={3}>
                    <StyledText
                      text={t("payments.quantity")}
                      isXtraSmall={isXtraSmall}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <StyledText
                      text={t("payments.item")}
                      isXtraSmall={isXtraSmall}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <StyledText
                      text={t("payments.unitPrice")}
                      isXtraSmall={isXtraSmall}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <StyledText
                      text={t("payments.amount")}
                      isXtraSmall={isXtraSmall}
                    />
                  </Grid>
                  {invoiceItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <GridItem xs={3} isXtraSmall={isXtraSmall}>
                        {item.quantity}
                      </GridItem>

                      <GridItem xs={4} isXtraSmall={isXtraSmall}>
                        {item.description.length
                          ? langConversionHelper.paymentSummaryFeeTransConversion(
                              item.description,
                              i18n.language.slice(0, 2)
                            )
                          : ""}
                      </GridItem>

                      <GridItem xs={3} isXtraSmall={isXtraSmall}>
                        <NumberFormat
                          value={item.unitPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$ "}
                          xs={1}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      </GridItem>
                      <GridItem xs={2} isXtraSmall={isXtraSmall}>
                        <NumberFormat
                          value={item.amount}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$ "}
                          xs={1}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      </GridItem>
                    </React.Fragment>
                  ))}
                </Grid>
                <Box mt={2}>
                  <Grid
                    container
                    sx={{
                      padding: isXtraSmall
                        ? "0 20px"
                        : isSmall
                        ? "0 40px"
                        : "0 100px",
                    }}
                  >
                    {summaryRows.map((row, index) => (
                      <SummaryRow
                        key={index}
                        label={row.label}
                        value={row.value}
                      />
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                paddingLeft: isXtraSmall ? 0 : "20px",
              }}
            >
              <SecurePaymentMethod
                paymentMethod={paymentMethod}
                validateSecurePaymentData={validateSecurePaymentData}
                setIsSuccess={setIsSuccess}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

SecurePaySummary.propTypes = {
  paymentMethod: PropTypes.array.isRequired,
  getInvoiceEstimateSummary: PropTypes.func.isRequired,
  setLoadingTime: PropTypes.func.isRequired,
  setIsSuccess: PropTypes.func.isRequired,
  loadingTime: PropTypes.bool,
  xs: PropTypes.number,
  isXtraSmall: PropTypes.bool,
  validateSecurePaymentData: PropTypes.object,
  text: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.number,
  children: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    paymentMethod: state.paymentMethod,
  };
}

const mapDispatchToProps = {
  getInvoiceEstimateSummary,
};

export default connect(mapStateToProps, mapDispatchToProps)(SecurePaySummary);
